import React, { useState, useEffect } from 'react';
import FormSteps from '../FormSteps';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { ReactComponent as VideoIcon } from '../../../../../assets/svg/Icon-Film.svg';
import { ReactComponent as PhotoIcon } from '../../../../../assets/svg/camera.svg';
import { ReactComponent as MusicIcon } from '../../../../../assets/svg/Icon-Sound.svg';
import { ReactComponent as DesignIcon } from '../../../../../assets/svg/Icon-Palette.svg';
import { ReactComponent as DigitalIcon } from '../../../../../assets/svg/Icon-UXUI.svg';
import { ReactComponent as LocationIcon } from '../../../../../assets/svg/pin.svg';
import { ReactComponent as GameIcon } from '../../../../../assets/svg/Icon-Console.svg';
import { ReactComponent as ArtIcon } from '../../../../../assets/svg/edit.svg';
import { ReactComponent as WritingIcon } from '../../../../../assets/svg/Icon-Lyric.svg';
import { ReactComponent as FashionIcon } from '../../../../../assets/svg/Icon-Costume.svg';
import { ReactComponent as LoveIcon } from '../../../../../assets/svg/Icon-Love.svg';
import { ReactComponent as BusinessIcon } from '../../../../../assets/svg/briefcase.svg';
import { ReactComponent as EventIcon } from '../../../../../assets/svg/calendar.svg';

const getCategoryAndIcon = (label: string): { category: string; icon: React.ReactNode } => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('video') || lowerLabel.includes('film') || lowerLabel.includes('cinema')) {
        return { category: 'video', icon: <VideoIcon /> };
    }
    if (lowerLabel.includes('photo') || lowerLabel.includes('camera')) {
        return { category: 'photography', icon: <PhotoIcon /> };
    }
    if (lowerLabel.includes('music') || lowerLabel.includes('audio') || lowerLabel.includes('sound')) {
        return { category: 'music', icon: <MusicIcon /> };
    }
    if (lowerLabel.includes('design') || lowerLabel.includes('illustration') || lowerLabel.includes('graphic')) {
        return { category: 'design', icon: <DesignIcon /> };
    }
    if (lowerLabel.includes('digital') || lowerLabel.includes('tech') || lowerLabel.includes('web')) {
        return { category: 'digital', icon: <DigitalIcon /> };
    }
    if (lowerLabel.includes('game') || lowerLabel.includes('gaming') || lowerLabel.includes('esport')) {
        return { category: 'gaming', icon: <GameIcon /> };
    }
    if (lowerLabel.includes('art') || lowerLabel.includes('artist') || lowerLabel.includes('paint') || lowerLabel.includes('visual')) {
        return { category: 'art', icon: <ArtIcon /> };
    }
    if (lowerLabel.includes('writ') || lowerLabel.includes('author') || lowerLabel.includes('literature') || lowerLabel.includes('poetry') || lowerLabel.includes('lyric')) {
        return { category: 'writing', icon: <WritingIcon /> };
    }
    if (lowerLabel.includes('fashion') || lowerLabel.includes('style') || lowerLabel.includes('costume') || lowerLabel.includes('clothing')) {
        return { category: 'fashion', icon: <FashionIcon /> };
    }
    if (lowerLabel.includes('social') || lowerLabel.includes('community') || lowerLabel.includes('people') || lowerLabel.includes('passion')) {
        return { category: 'social', icon: <LoveIcon /> };
    }
    if (lowerLabel.includes('business') || lowerLabel.includes('brand') || lowerLabel.includes('company') || lowerLabel.includes('corporate')) {
        return { category: 'business', icon: <BusinessIcon /> };
    }
    if (lowerLabel.includes('event') || lowerLabel.includes('festival') || lowerLabel.includes('concert') || lowerLabel.includes('show')) {
        return { category: 'event', icon: <EventIcon /> };
    }
    const trimmedLabel = label.trim();
    const isCapitalized = /^[A-Z]/.test(trimmedLabel);
    const isShortProperNoun = trimmedLabel.split(' ').length <= 3;
    if ((
        lowerLabel.includes('based') || lowerLabel.includes('location') ||
        lowerLabel.includes('city') || lowerLabel.includes('country') ||
        lowerLabel.includes('travel') || lowerLabel.includes('local') ||
        (isCapitalized && isShortProperNoun && trimmedLabel.split(' ').length === 1))) {
        return { category: 'location', icon: <LocationIcon /> };
    }
    return { category: 'other', icon: null };
};

const GENERATE_BRAND_TAGS = gql`
  query GenerateBrandTags($brandInfo: String!, $userContext: UserContextInput) {
    generateBrandTags(brandInfo: $brandInfo, userContext: $userContext)
  }
`;

const REFINE_BRAND_INFO = gql`
  mutation RefineBrandInfo($originalText: String!, $selectedTags: [String!]!) {
    refineBrandInfo(originalText: $originalText, selectedTags: $selectedTags) {
      refinedText
    }
  }
`;

interface BrandInfoStepProps {
    formik: any;
    goToNextStep: () => void;
    skipStep: () => void;
}

interface Tag {
    id: string;
    label: string;
    category?: string;
    icon?: React.ReactNode;
}

const BrandInfoStep: React.FC<BrandInfoStepProps> = ({
    formik,
    goToNextStep,
    skipStep
}) => {
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isRefining, setIsRefining] = useState(false);
    const [refinedPreview, setRefinedPreview] = useState<string | null>(null);

    const [generateTags] = useLazyQuery(GENERATE_BRAND_TAGS, {
        onCompleted: (data) => {
            if (data?.generateBrandTags) {
                const tags = data.generateBrandTags.map((label: string) => {
                    const { category, icon } = getCategoryAndIcon(label);
                    return {
                        id: label.toLowerCase().replace(/\s+/g, '-'),
                        label,
                        category,
                        icon
                    };
                });
                setAvailableTags(tags);
            }
            setIsLoadingTags(false);
        }
    });

    const [refineBrandInfo] = useMutation(REFINE_BRAND_INFO, {
        onCompleted: (data) => {
            if (data?.refineBrandInfo?.refinedText) {
                setRefinedPreview(data.refineBrandInfo.refinedText);
            }
            setIsRefining(false);
        },
        onError: () => {
            toast.error('Failed to refine. Please try again.');
            setIsRefining(false);
        }
    });

    useEffect(() => {
        setIsLoadingTags(true);
        generateTags({
            variables: {
                brandInfo: 'creative brand, film production, content creation'
            }
        });
    }, []);

    useEffect(() => {
        const brandInfo = formik.values.brandInfo;
        if (!brandInfo || brandInfo.trim().length === 0) return;

        const timer = setTimeout(() => {
            setIsLoadingTags(true);
            generateTags({ variables: { brandInfo } });
        }, 1000);

        return () => clearTimeout(timer);
    }, [formik.values.brandInfo]);

    const handleRefine = async () => {
        const originalText = formik.values.brandInfo || '';
        const selectedTagLabels = availableTags
            .filter(tag => selectedTags.includes(tag.id))
            .map(tag => tag.label);

        if (!originalText.trim() && selectedTagLabels.length === 0) {
            toast.info('Please enter some text or select tags first');
            return;
        }

        setIsRefining(true);
        await refineBrandInfo({
            variables: {
                originalText,
                selectedTags: selectedTagLabels
            }
        });
    };

    const handleAcceptRefined = () => {
        if (refinedPreview) {
            formik.setFieldValue('brandInfo', refinedPreview);
            toast.success('Refined text applied!');
            setRefinedPreview(null);
        }
    };

    const handleRejectRefined = () => {
        setRefinedPreview(null);
    };

    const handleNext = () => {
        // Save selected tags to formik
        formik.setFieldValue('brandTags', selectedTags);
        goToNextStep();
    };

    const toggleTag = (tagId: string) => {
        setSelectedTags(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    return (
        <div className="infostep-custom-header">
            <FormSteps
                header="Add brand info"
                description="Your brand info helps us personalize your experience."
                nextButton={handleNext}
                skipButton={skipStep}
            >
                <div className="field mb-5 brand-info-field">
                    <div className="is-flex">
                        <button
                            type="button"
                            className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-medium is-primary ${isRefining ? 'is-loading' : ''}`}
                            onClick={handleRefine}
                            disabled={isRefining || isLoadingTags}
                        >
                            {isRefining ? '' : 'Refine With AI'}
                        </button>
                    </div>
                    <div className="field-body">
                        <textarea
                            className="textarea brand-info-textarea"
                            name="brandInfo"
                            placeholder="Add brand info..."
                            rows={4}
                            value={formik.values.brandInfo || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isRefining}
                        />
                    </div>
                </div>

                {refinedPreview && (
                    <div className="refined-preview-container">
                        <div className="refined-preview-card">
                            <div className="refined-preview-header is-flex">
                                <span className="refined-preview-title">
                                    AI Suggestion
                                </span>
                                <button
                                    type="button"
                                    onClick={handleRejectRefined}
                                    className="refined-preview-close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="refined-preview-text">
                                {refinedPreview}
                            </div>

                            <div className="refined-preview-actions is-flex">
                                <button
                                    type="button"
                                    onClick={handleAcceptRefined}
                                    className="button-next refined-button"
                                >
                                    Apply Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRejectRefined}
                                    className="button-skip refined-button"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="brand-tags-section">
                    <div className="ai-tag-text">
                        <span>Add more with AI </span>
                    </div>

                    <div className="brand-tags-container">
                        {availableTags.map(tag => (
                            <button
                                key={tag.id}
                                type="button"
                                className={`brand-tag ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                                onClick={() => toggleTag(tag.id)}
                                disabled={isLoadingTags || isRefining}
                            >
                                {tag.icon ? (
                                    <span>{tag.icon}</span>
                                ) : (
                                    <span className="tag-action-icon"></span>
                                )}
                                <span className="tag-label">{tag.label}</span>
                                <span className="tag-action-icon">
                                    {selectedTags.includes(tag.id) ? '–' : '+'}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </FormSteps>
        </div>
    );
};

export default BrandInfoStep;